import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { MarketingShell } from "@/pages/Marketing";
import {
  Rocket, ListChecks, UsersThree, PaperPlaneTilt, DeviceMobile, Fingerprint,
  ChartLineUp, Trophy, CreditCard, ShieldCheck, Question, ArrowRight, Check, LockKey, MagnifyingGlass,
} from "@phosphor-icons/react";

const IMG = {
  build: "/guide/build.jpg",
  roll: "/guide/roll.jpg",
  nominations: "/guide/nominations.jpg",
  voting: "/guide/voting.jpg",
  live: "/guide/live.jpg",
  results: "/guide/results.jpg",
};

// Building blocks -------------------------------------------------------------
const P = ({ children }) => <p className="text-[15px] leading-relaxed text-zinc-600">{children}</p>;

const Shot = ({ src, caption }) => (
  <figure className="mt-5">
    <div className="rounded-xl overflow-hidden border border-zinc-200 shadow-sm bg-white">
      <img src={src} alt={caption} loading="lazy" className="w-full block" />
    </div>
    {caption && <figcaption className="text-xs text-zinc-400 mt-2 font-mono">{caption}</figcaption>}
  </figure>
);

const Steps = ({ items }) => (
  <ol className="mt-4 space-y-3">
    {items.map((it, i) => (
      <li key={i} className="flex gap-3">
        <span className="shrink-0 w-6 h-6 rounded-full bg-black text-white text-xs font-bold flex items-center justify-center mt-0.5">{i + 1}</span>
        <span className="text-[15px] leading-relaxed text-zinc-700">{it}</span>
      </li>
    ))}
  </ol>
);

const Bullets = ({ items }) => (
  <ul className="mt-4 space-y-2">
    {items.map((it, i) => (
      <li key={i} className="flex gap-2.5 text-[15px] leading-relaxed text-zinc-700">
        <Check size={18} weight="bold" className="text-[#1F6FE5] shrink-0 mt-0.5" />
        <span>{it}</span>
      </li>
    ))}
  </ul>
);

const Who = ({ role, children }) => {
  const map = {
    Organiser: "bg-blue-50 text-blue-700 border-blue-200",
    Voter: "bg-emerald-50 text-emerald-700 border-emerald-200",
    Nominee: "bg-amber-50 text-amber-800 border-amber-200",
  };
  return (
    <div className={`mt-4 rounded-lg border px-4 py-3 ${map[role]}`}>
      <span className="text-[10px] font-mono uppercase tracking-widest">For {role.toLowerCase()}s</span>
      <p className="text-sm leading-relaxed mt-1 text-zinc-700">{children}</p>
    </div>
  );
};

const FAQ = [
  ["Do voters need to create an account?", "No. Voters tap a single-use link (or dial a USSD code) — there are no passwords or sign-ups."],
  ["Can the organiser see how someone voted?", "No. Ballots are stored anonymously with no link to the voter. Only eligibility and “has voted” are tracked against a person."],
  ["What stops someone voting twice?", "Each voter has a single-use token, and the vote is claimed atomically — verified to hold even with thousands voting at the same instant."],
  ["Can I collect nominations before choosing candidates?", "Yes — run a nomination round, review submissions by position, and approve nominees straight into candidates."],
  ["How much does it cost?", "You pay per election based on roll size. Use the estimator on the Pricing page for an exact figure; 15,000+ rolls get a custom quote."],
  ["Can voters without smartphones take part?", "Yes — feature-phone voters use USSD, and in-person voters can use a supervised kiosk."],
];

const FaqList = ({ items }) => (
  <div className="mt-4 divide-y divide-zinc-100 border border-zinc-200 rounded-lg overflow-hidden">
    {items.length === 0 && <div className="p-4 text-sm text-zinc-400">No matching questions.</div>}
    {items.map(([q, a], i) => (
      <div key={i} className="p-4" data-testid={`faq-${i}`}>
        <div className="font-semibold text-[15px] text-zinc-900">{q}</div>
        <div className="text-[15px] leading-relaxed text-zinc-600 mt-1">{a}</div>
      </div>
    ))}
  </div>
);

// Content ---------------------------------------------------------------------
const SECTIONS = [
  {
    id: "getting-started", icon: Rocket, title: "Getting started",
    blurb: "Create your account and organisation, and understand how SelectFlow works.",
    render: () => (
      <>
        <P>SelectFlow lets any organisation run a secure, secret-ballot election end to end — from building the voter roll to publishing a verifiable result. There's nothing to install; everything runs in the browser, and voters never need an account.</P>
        <Steps items={[
          <>Create your free account and organisation. You're the first admin.</>,
          "Invite colleagues from Settings → Team if you want co-organisers.",
          "Create your first election from the dashboard. You only pay per election, based on your voter-roll size — see the Billing section.",
        ]} />
        <Who role="Organiser">You can explore everything risk-free first: click “View live demo” on the homepage to browse a fully seeded, read-only election.</Who>
      </>
    ),
  },
  {
    id: "build-election", icon: ListChecks, title: "Building your election",
    blurb: "Add positions and candidates, set the schedule and results visibility.",
    render: () => (
      <>
        <P>An election is made of <strong>positions</strong> (the seats being contested) and <strong>candidates</strong> under each position. Open an election and use the <em>Positions &amp; Candidates</em> tab.</P>
        <Shot src={IMG.build} caption="Election setup — positions with candidates underneath" />
        <Steps items={[
          "Add each position (e.g. President, Secretary). Choose single-choice or ranked (Borda) voting and how many selections a voter may make.",
          "Add candidates under each position — name, short bio and an optional photo.",
          "Set the opening and closing date/time and your timezone.",
          "Decide when results become visible: live, or only after voting closes.",
        ]} />
        <P>You can keep editing while the election is a <strong>draft</strong>. Once you open voting, the ballot is locked so it can't change under voters.</P>
      </>
    ),
  },
  {
    id: "voter-roll", icon: UsersThree, title: "Building the voter roll",
    blurb: "Import by CSV, add voters manually, or open self-registration.",
    render: () => (
      <>
        <P>Only people on your roll can vote, and each person votes once. There are three ways to build it — mix and match:</P>
        <Shot src={IMG.roll} caption="Voter roll — import CSV, add manually, or share a self-registration link" />
        <Bullets items={[
          "CSV import — upload a spreadsheet of names, emails and/or phone numbers in one go.",
          "Add manually — enter voters one at a time for small elections.",
          "Self-registration link — share a public link and let voters register themselves; you approve them.",
        ]} />
        <Who role="Voter">If self-registration is on, you'll open a link, enter your details, and — once approved — receive your private voting link by email or SMS.</Who>
      </>
    ),
  },
  {
    id: "nominations", icon: PaperPlaneTilt, title: "Collecting nominations",
    blurb: "Run a nomination round, set a deadline, review and promote nominees.",
    render: () => (
      <>
        <P>Sometimes candidates aren't decided yet. A <strong>nomination round</strong> collects proposed names first, which you then approve into candidates on the real election.</P>
        <Shot src={IMG.nominations} caption="Nominations grouped by position, with a deadline countdown and one-click Approve all" />
        <Steps items={[
          "Create a second election to act as the round, then in its Overview link it to the target election (“nominates for”). It now shows a Nominations tab instead of a voter roll.",
          "Optionally set a Nomination deadline — after that moment the public link closes automatically, with a live countdown shown to nominators.",
          "Share the nomination link. People submit names for each position (nominating themselves or others).",
          "Review submissions in the Nominations tab, grouped by position. Approve the ones you want — or use Approve all to promote a whole slate.",
        ]} />
        <P>Approving a nominee <strong>promotes them straight into the target election as a candidate</strong> under the position they were nominated for.</P>
        <Who role="Nominee">You'll see a simple form for the position you're nominated for. If a fee applies, you'll be guided through payment before your nomination is recorded.</Who>
      </>
    ),
  },
  {
    id: "channels", icon: DeviceMobile, title: "Invitations & voting channels",
    blurb: "Reach voters by web link, USSD on feature phones, or in-person kiosk.",
    render: () => (
      <>
        <P>Every voter gets a unique, single-use link — no passwords, no accounts. SelectFlow meets voters wherever they are:</P>
        <Bullets items={[
          "Web / smartphone — tap the link in an email or SMS and vote on a phone-first ballot.",
          "USSD — feature-phone voters dial a short code and vote through a menu, no internet needed.",
          "Kiosk — for in-person voting, a supervised device accepts per-voter kiosk codes.",
        ]} />
        <P>Invitations send by email (and SMS where a number is present). Bounced or complained addresses are automatically suppressed so your sender reputation stays healthy.</P>
      </>
    ),
  },
  {
    id: "voting", icon: Fingerprint, title: "Voting & anonymity",
    blurb: "How the secret, unlinkable ballot works — and why no one votes twice.",
    render: () => (
      <>
        <P>This is the heart of SelectFlow. Ballots are <strong>secret and unlinkable</strong>: the moment a vote is cast, it is stored with no connection back to the voter's identity.</P>
        <Shot src={IMG.voting} caption="A phone-first secret ballot — single-choice or ranked, with a receipt code" />
        <Bullets items={[
          "Single-choice or ranked ballots, with configurable maximum selections per position.",
          "Double-voting is blocked atomically — even under heavy simultaneous load, no one votes twice.",
          "Each voter gets a receipt code confirming their vote was counted, without revealing their choices.",
        ]} />
        <Who role="Voter">Your identity is used only to check you're eligible and haven't already voted. How you voted is never stored against your name — not even the organiser can see it.</Who>
      </>
    ),
  },
  {
    id: "live", icon: ChartLineUp, title: "Watching turnout live",
    blurb: "A real-time turnout board split by web, USSD and kiosk.",
    render: () => (
      <>
        <P>While voting is open, a live monitor shows turnout updating in real time, split by channel. Share the read-only monitor link with your electoral committee so everyone watches the same numbers.</P>
        <Shot src={IMG.live} caption="Live turnout monitor — real-time percentage and channel split" />
      </>
    ),
  },
  {
    id: "results", icon: Trophy, title: "Closing & results",
    blurb: "Instant winners, exports, and a one-click integrity report.",
    render: () => (
      <>
        <P>When the closing time passes (or you close early), results compute instantly.</P>
        <Shot src={IMG.results} caption="Results with clear winners and a one-click PDF integrity report" />
        <Bullets items={[
          "Clear winners per position, with transparent tie handling.",
          "Export results and the voter roll as Excel or PDF.",
          "One-click Integrity Report (PDF) — a tamper-evident summary showing turnout, channel splits and audit-chain verification you can hand to any committee.",
        ]} />
      </>
    ),
  },
  {
    id: "billing", icon: CreditCard, title: "Billing & pricing",
    blurb: "Pay per election by roll size — with PDF receipts.",
    render: () => (
      <>
        <P>You pay per election, priced by the size of your voter roll — no subscriptions, no surprises. Drag the estimator on the <Link to="/pricing" className="text-[#1F6FE5] font-medium underline">Pricing page</Link> to see your exact price for any roll size.</P>
        <Bullets items={[
          "Transparent GHS bands with a flat minimum for small rolls.",
          "Very large rolls (15,000+) get a tailored institutional quote.",
          "Every payment produces a PDF receipt, available anytime under Settings → Billing history.",
        ]} />
      </>
    ),
  },
  {
    id: "security", icon: ShieldCheck, title: "Security & your data",
    blurb: "Anonymity, a tamper-evident audit chain, roles, and full backups.",
    render: () => (
      <>
        <P>SelectFlow is built to e-voting standards and is strictly multi-tenant — your organisation's data is isolated from every other.</P>
        <Bullets items={[
          "Single-use cryptographic voting tokens; ballots stored with no voter linkage.",
          "A hash-chained audit log makes tampering detectable end to end.",
          "Role-based access (admin vs organiser) and secure, password-less voter access.",
          "Full tenant backup: export all your data as a ZIP anytime from Settings → Data backup.",
        ]} />
        <P>Read the deeper <Link to="/security" className="text-[#1F6FE5] font-medium underline">Security overview</Link> for the full model.</P>
      </>
    ),
  },
  {
    id: "faq", icon: Question, title: "FAQ",
    blurb: "Quick answers to the questions we hear most.",
    render: () => <FaqList items={FAQ} />,
  },
];

export const GUIDE_SECTIONS = SECTIONS;

// The full walkthrough (TOC + sections). Shell-agnostic — used inside the app.
export function GuideContent() {
  const [active, setActive] = useState(SECTIONS[0].id);
  const [q, setQ] = useState("");
  const refs = useRef({});
  const ql = q.trim().toLowerCase();

  const faqMatches = FAQ.filter(([question, a]) => (question + " " + a).toLowerCase().includes(ql));
  const visible = SECTIONS.filter((s) => {
    if (!ql) return true;
    if ((s.title + " " + (s.blurb || "")).toLowerCase().includes(ql)) return true;
    if (s.id === "faq" && faqMatches.length > 0) return true;
    return false;
  });

  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) setActive(e.target.id); }),
      { rootMargin: "-96px 0px -70% 0px", threshold: 0 }
    );
    Object.values(refs.current).forEach((el) => el && obs.observe(el));
    return () => obs.disconnect();
  }, [ql]);

  // Deep-link support: scroll to #section on load.
  useEffect(() => {
    const hash = window.location.hash.replace("#", "");
    if (hash && refs.current[hash]) {
      setTimeout(() => window.scrollTo({ top: refs.current[hash].offsetTop - 88, behavior: "smooth" }), 200);
    }
  }, []);

  const jump = (id) => {
    const el = refs.current[id];
    if (el) window.scrollTo({ top: el.offsetTop - 88, behavior: "smooth" });
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[240px_1fr] gap-10">
      <aside className="lg:sticky lg:top-24 self-start" data-testid="guide-toc">
        <div className="relative mb-4">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <input
            data-testid="guide-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search the guide…"
            className="w-full pl-9 pr-3 py-2 text-sm border border-zinc-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-[#1F6FE5]/30"
          />
        </div>
        <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-400 mb-3">On this page</div>
        <nav className="flex lg:flex-col gap-1 overflow-x-auto lg:overflow-visible">
          {visible.map((s) => (
            <button
              key={s.id}
              data-testid={`toc-${s.id}`}
              onClick={() => jump(s.id)}
              className={`text-left whitespace-nowrap lg:whitespace-normal flex items-center gap-2 px-3 py-2 rounded-md text-sm transition-colors ${active === s.id ? "bg-black text-white font-medium" : "text-zinc-500 hover:text-black hover:bg-zinc-100"}`}
            >
              <s.icon size={16} weight="duotone" className={active === s.id ? "text-white" : "text-[#1F6FE5]"} />
              {s.title}
            </button>
          ))}
        </nav>
      </aside>

      <div className="min-w-0 space-y-16">
        {visible.length === 0 && (
          <p data-testid="guide-no-results" className="text-zinc-500">No results for “{q}”. Try a different word.</p>
        )}
        {visible.map((s) => (
          <section key={s.id} id={s.id} data-testid={`section-${s.id}`} ref={(el) => (refs.current[s.id] = el)} className="scroll-mt-24">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center shrink-0">
                <s.icon size={22} weight="duotone" className="text-[#1F6FE5]" />
              </div>
              <h2 className="font-display font-bold text-2xl tracking-tight">{s.title}</h2>
            </div>
            {s.id === "faq" ? <FaqList items={ql ? faqMatches : FAQ} /> : s.render()}
          </section>
        ))}
        {!ql && (
          <div className="border-t border-zinc-200 pt-10">
            <h3 className="font-display font-bold text-xl tracking-tight mb-2">Ready to run yours?</h3>
            <p className="text-zinc-600 mb-5">Set up your first election in minutes — you only pay when you're ready to open voting.</p>
            <Link data-testid="guide-cta-start" to="/app" className="inline-flex items-center gap-2 px-5 py-3 bg-black text-white rounded-md text-sm font-semibold hover:bg-zinc-800 transition-colors active:scale-95">
              Go to your dashboard <ArrowRight size={16} weight="bold" />
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}

// PUBLIC OVERVIEW (homepage links here). The full walkthrough is login-only.
export default function Guide() {
  return (
    <MarketingShell>
      <section className="max-w-6xl mx-auto px-6 pt-20 pb-8">
        <div className="text-xs font-mono uppercase tracking-widest text-[#1F6FE5] mb-4">Guide</div>
        <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[0.98] max-w-3xl">
          How SelectFlow runs an <span className="text-[#1F6FE5]">election</span>, start to finish.
        </h1>
        <p className="text-lg text-zinc-600 leading-relaxed max-w-2xl mt-6">
          A quick overview of everything the platform does. The full step-by-step walkthrough — with screenshots for every stage — lives inside your account.
        </p>
      </section>

      <section className="max-w-6xl mx-auto px-6 pb-6">
        <div className="grid sm:grid-cols-2 gap-4">
          {SECTIONS.filter((s) => s.id !== "faq").map((s) => (
            <div key={s.id} data-testid={`overview-${s.id}`} className="flex gap-4 bg-white border border-zinc-200 p-5 hover:-translate-y-0.5 hover:shadow-md transition-all">
              <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center shrink-0">
                <s.icon size={22} weight="duotone" className="text-[#1F6FE5]" />
              </div>
              <div>
                <h3 className="font-display font-semibold tracking-tight">{s.title}</h3>
                <p className="text-sm text-zinc-500 leading-relaxed mt-1">{s.blurb}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-6 pb-24">
        <div className="bg-[#0B1220] text-white rounded-2xl p-8 sm:p-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6" data-testid="guide-locked-cta">
          <div className="flex gap-4 max-w-xl">
            <LockKey size={30} weight="duotone" className="text-[#7CA9F9] shrink-0 mt-1" />
            <div>
              <h3 className="font-display font-bold text-xl tracking-tight">The full walkthrough is in your account</h3>
              <p className="text-sm text-zinc-300 leading-relaxed mt-1">Sign in to read the complete guide with annotated screenshots for every stage — setup, nominations, voting, live turnout and results.</p>
            </div>
          </div>
          <div className="flex flex-wrap gap-3 shrink-0">
            <Link data-testid="guide-signin" to="/auth" className="inline-flex items-center gap-2 px-5 py-3 bg-white text-black rounded-md text-sm font-semibold hover:bg-zinc-200 transition-colors">Sign in to read it</Link>
            <Link data-testid="guide-start" to="/auth?mode=register" className="inline-flex items-center gap-2 px-5 py-3 border border-white/30 rounded-md text-sm font-semibold hover:bg-white/10 transition-colors">Start free <ArrowRight size={16} weight="bold" /></Link>
          </div>
        </div>
      </section>
    </MarketingShell>
  );
}
